import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppFormDialog } from '@/components/apps/AppFormDialog';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from '@/hooks/use-toast';
import type { AppRecord } from '@/lib/types';

async function fetchApps(): Promise<AppRecord[]> {
  const { data } = await api.get<AppRecord[]>('/v1/apps');
  return data;
}

export default function Apps() {
  const qc = useQueryClient();
  const apps = useQuery({ queryKey: queryKeys.apps, queryFn: fetchApps });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AppRecord | null>(null);

  const archive = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/apps/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.apps });
      toast({ title: 'App archived' });
    },
    onError: () =>
      toast({ title: 'Could not archive app', variant: 'destructive' }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Apps</h1>
          <p className="text-sm text-muted-foreground">
            Each app holds its store URLs and the bundle IDs we bind your publishable key to.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus />
          New App
        </Button>
      </header>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Android package</TableHead>
              <TableHead>iOS bundle</TableHead>
              <TableHead>URLs</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {apps.isLoading &&
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            {apps.isError && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-destructive">
                  Could not load apps.
                </TableCell>
              </TableRow>
            )}
            {apps.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No apps yet. Click <span className="font-medium">+ New App</span> to register one.
                </TableCell>
              </TableRow>
            )}
            {apps.data?.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <div className="font-medium">{a.name}</div>
                  {a.description && (
                    <div className="text-xs text-muted-foreground">{a.description}</div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{a.androidPackage ?? '—'}</TableCell>
                <TableCell className="font-mono text-xs">{a.iosBundleId ?? '—'}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {a.androidUrl && <Badge variant="secondary">Android</Badge>}
                    {a.iosUrl && <Badge variant="secondary">iOS</Badge>}
                    {a.webUrl && <Badge variant="secondary">Web</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  {a.archived ? (
                    <Badge variant="outline">Archived</Badge>
                  ) : (
                    <Badge>Active</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          setEditing(a);
                          setOpen(true);
                        }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={a.archived || archive.isPending}
                        onSelect={() => archive.mutate(a.id)}
                      >
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AppFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
      />
    </div>
  );
}
